/**
 * Chain identity for the admin panel.
 *
 * WHY THIS EXISTS
 * The chain a thing lives on now decides three separate presentation questions — what to
 * call it, which explorer to link to, and whether an address is even valid — and each was
 * about to be answered by a literal pasted into whichever component needed it. The label map
 * was already duplicated between AddAssetDialog and TableColumns before this file, which is
 * how the panel ends up showing "AVALANCHE_FUJI" in one place and "Avalanche Fuji" in
 * another.
 *
 * WHAT THIS IS NOT
 * Not a source of truth. The backend registry decides which chains exist, which have
 * contracts, and which have a CRE mint workflow — creating an asset on a chain that is
 * missing either is rejected server-side with a message naming what is absent. This is
 * presentation only, and an id it does not recognise is rendered rather than hidden, so a
 * chain added on the backend degrades to showing its raw id instead of disappearing.
 */

export type ChainFamily = 'EVM' | 'STELLAR';

export interface ChainPresentation {
  id: string;
  label: string;
  family: ChainFamily;
  /** Prefix; append an address. Empty when the chain has no explorer configured. */
  explorerAddressPrefix: string;
  /** Prefix; append a transaction hash. */
  explorerTxPrefix: string;
  /** Whether an asset can be created on this chain from the panel. */
  issuable: boolean;
}

export const CHAINS: Record<string, ChainPresentation> = {
  AVALANCHE_FUJI: {
    id: 'AVALANCHE_FUJI',
    label: 'Avalanche Fuji',
    family: 'EVM',
    explorerAddressPrefix: 'https://testnet.snowtrace.io/address/',
    explorerTxPrefix: 'https://testnet.snowtrace.io/tx/',
    issuable: true,
  },
  AVALANCHE_MAINNET: {
    id: 'AVALANCHE_MAINNET',
    label: 'Avalanche',
    family: 'EVM',
    explorerAddressPrefix: 'https://snowtrace.io/address/',
    explorerTxPrefix: 'https://snowtrace.io/tx/',
    issuable: true,
  },
  POLYGON_AMOY: {
    id: 'POLYGON_AMOY',
    label: 'Polygon Amoy',
    family: 'EVM',
    explorerAddressPrefix: 'https://amoy.polygonscan.com/address/',
    explorerTxPrefix: 'https://amoy.polygonscan.com/tx/',
    issuable: true,
  },
  POLYGON_MAINNET: {
    id: 'POLYGON_MAINNET',
    label: 'Polygon',
    family: 'EVM',
    explorerAddressPrefix: 'https://polygonscan.com/address/',
    explorerTxPrefix: 'https://polygonscan.com/tx/',
    issuable: true,
  },
};

/**
 * The chains an asset can be created on, in the order they should appear in a picker.
 *
 * A chain listed here still has to pass the backend's checks — it needs contracts AND a CRE
 * mint workflow — so this list being longer than what is actually deployable is expected and
 * safe: the failure is a 400 that names the missing piece, not a token on the wrong network.
 */
export const ISSUABLE_CHAINS = Object.values(CHAINS).filter(
  (chain) => chain.issuable,
);

/** Display name for a chain id. Falls back to the raw id, never to blank. */
export function chainLabel(chain: string | null | undefined): string {
  if (!chain) return 'N/A';
  return CHAINS[chain]?.label ?? chain;
}

/** Explorer link for an address on a chain, or null when the chain is unknown. */
export function explorerAddressUrl(
  chain: string | null | undefined,
  address: string | null | undefined,
): string | null {
  if (!chain || !address) return null;
  const prefix = CHAINS[chain]?.explorerAddressPrefix;
  return prefix ? `${prefix}${address}` : null;
}

/** Explorer link for a transaction on a chain, or null when the chain is unknown. */
export function explorerTxUrl(
  chain: string | null | undefined,
  txHash: string | null | undefined,
): string | null {
  if (!chain || !txHash) return null;
  const prefix = CHAINS[chain]?.explorerTxPrefix;
  return prefix ? `${prefix}${txHash}` : null;
}

/**
 * Is this a well-formed address for the chain?
 *
 * Every chain in the registry is EVM today, so this is one check — but it is written against
 * the family rather than against "is it 0x..." because that is the assumption that silently
 * breaks first. A Stellar G-address is 56 characters of base32; an EVM validator accepts
 * none of it, and a Stellar validator accepts no EVM address, so a transfer form that
 * validates against the wrong family either blocks a valid address or accepts one the
 * network cannot deliver to.
 */
export function isValidAddressForChain(
  chain: string | null | undefined,
  address: string,
): boolean {
  const trimmed = address.trim();
  if (!trimmed) return false;

  const family = chain ? CHAINS[chain]?.family : 'EVM';
  switch (family ?? 'EVM') {
    case 'EVM':
      return /^0x[0-9a-fA-F]{40}$/.test(trimmed);
    case 'STELLAR':
      return /^G[A-Z2-7]{55}$/.test(trimmed);
    default:
      return false;
  }
}

/**
 * Shorten an address for display, correctly for its chain.
 *
 * The chain matters even for the truncation: the existing helpers assume a 42-character EVM
 * address, and applying that shape to a 56-character Stellar address cuts it in the wrong
 * places. Keeping the length rule beside the family is what stops that from being discovered
 * visually, later.
 */
export function truncateAddress(
  chain: string | null | undefined,
  address: string | null | undefined,
): string {
  if (!address) return '';
  const family = (chain ? CHAINS[chain]?.family : 'EVM') ?? 'EVM';
  const lead = family === 'STELLAR' ? 6 : 6;
  const tail = family === 'STELLAR' ? 6 : 4;
  if (address.length <= lead + tail + 3) return address;
  return `${address.slice(0, lead)}...${address.slice(-tail)}`;
}
